import { type FC, Fragment } from 'react';
import clsx from 'clsx';
import styles from './VulnerabilityManagement.module.css';

interface VulnerabilityTableProps {
  vulnerabilities: any[];
  filteredCount: number;
  selectedItems: string[];
  sortField: string;
  sortDirection: string;
  currentPage: number;
  totalPages: number;
  indexOfFirstItem: number;
  indexOfLastItem: number;
  onSort: (field: string) => void;
  onSelectItem: (id: string) => void;
  onSelectAll: () => void;
  onOpenDetail: (vulnerability: any) => void;
  onPageChange: (page: number) => void;
  onNavigate: (path: string) => void;
}

export const VulnerabilityTable: FC<VulnerabilityTableProps> = ({
  vulnerabilities,
  filteredCount,
  selectedItems,
  sortField,
  sortDirection,
  currentPage,
  totalPages,
  indexOfFirstItem,
  indexOfLastItem,
  onSort,
  onSelectItem,
  onSelectAll,
  onOpenDetail,
  onPageChange,
  onNavigate,
}) => {
  return (
    <>
      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead className={styles.tableHeader}>
            <tr>
              <th className={styles.tableHeaderCell}>
                <input
                  type="checkbox"
                  checked={selectedItems.length === vulnerabilities.length && vulnerabilities.length > 0}
                  onChange={onSelectAll}
                />
              </th>
              <th className={styles.tableHeaderCell} data-sortable="true" onClick={() => onSort('severity')}>
                Severity {sortField === 'severity' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th className={styles.tableHeaderCell}>ID</th>
              <th className={styles.tableHeaderCell} data-sortable="true" onClick={() => onSort('title')}>
                Title {sortField === 'title' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th className={styles.tableHeaderCell}>Endpoint</th>
              <th className={styles.tableHeaderCell}>Type</th>
              <th className={styles.tableHeaderCell}>Status</th>
              <th className={styles.tableHeaderCell}>Test</th>
              <th className={styles.tableHeaderCell}>Hypothesis</th>
              <th className={styles.tableHeaderCell} data-sortable="true" onClick={() => onSort('foundDate')}>
                Found Date {sortField === 'foundDate' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
            </tr>
          </thead>
          <tbody>
            {vulnerabilities.map((vuln) => (
              <tr key={vuln.id} className={styles.tableRow} onClick={() => onOpenDetail(vuln)}>
                <td className={styles.tableCell} onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selectedItems.includes(vuln.id)}
                    onChange={() => onSelectItem(vuln.id)}
                  />
                </td>
                <td className={styles.tableCell}>
                  <span className={styles.severityBadge} data-severity={vuln.severity?.toLowerCase()}>
                    {vuln.severity}
                  </span>
                </td>
                <td className={styles.tableCell}>
                  {vuln.source_file
                    ? vuln.source_file.replace('.md', '').replace('vuln-', '')
                    : vuln.id?.substring(0, 12)}
                </td>
                <td className={clsx(styles.tableCell, styles.titleCell)}>{vuln.title}</td>
                <td className={styles.tableCell}>{vuln.endpoint}</td>
                <td className={styles.tableCell}>{vuln.vulnerability_type || vuln.type}</td>
                <td className={styles.tableCell}>
                  <span className={styles.statusBadge} data-status={vuln.status?.toLowerCase()}>
                    {vuln.status?.replace('_', ' ')}
                  </span>
                </td>
                <td className={styles.tableCell} onClick={(e) => e.stopPropagation()}>
                  {vuln.test_id && vuln.test_id !== 'manual' && vuln.schema_id ? (
                    <span
                      className={clsx(styles.linkCell, styles.linkCellClickable)}
                      onClick={() => onNavigate(`/tests/${vuln.schema_id}/${vuln.test_id}-test.py`)}
                    >
                      {vuln.test_id}
                    </span>
                  ) : (
                    <span className={styles.linkCell}>—</span>
                  )}
                </td>
                <td className={styles.tableCell} onClick={(e) => e.stopPropagation()}>
                  {vuln.hypothesis_filename && vuln.schema_id ? (
                    <span
                      className={clsx(styles.linkCell, styles.linkCellClickable)}
                      onClick={() => onNavigate(`/hypotheses/${vuln.schema_id}/${vuln.hypothesis_filename}`)}
                    >
                      {vuln.hypothesis_filename.replace('.json', '')}
                    </span>
                  ) : vuln.test_id && vuln.test_id !== 'manual' && vuln.schema_id ? (
                    <span
                      className={clsx(styles.linkCell, styles.linkCellClickable)}
                      onClick={() => onNavigate(`/hypotheses/${vuln.schema_id}/${vuln.test_id}.json`)}
                    >
                      {vuln.test_id}
                    </span>
                  ) : (
                    <span className={styles.linkCell}>—</span>
                  )}
                </td>
                <td className={styles.tableCell}>
                  {new Date(vuln.first_found_date || vuln.foundDate || vuln.created_at).toLocaleDateString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className={styles.pagination}>
        <div className={styles.paginationInfo}>
          Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredCount)} of {filteredCount}{' '}
          vulnerabilities
        </div>
        <div className={styles.paginationButtons}>
          <button
            className={styles.pageButton}
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter((page) => page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1)
            .map((page, index, arr) => (
              <Fragment key={page}>
                {index > 0 && arr[index - 1] !== page - 1 && (
                  <button className={styles.pageButton} disabled>
                    ...
                  </button>
                )}
                <button
                  className={clsx(styles.pageButton, {
                    [styles.pageButtonActive]: currentPage === page,
                  })}
                  onClick={() => onPageChange(page)}
                >
                  {page}
                </button>
              </Fragment>
            ))}
          <button
            className={styles.pageButton}
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      </div>
    </>
  );
};
